import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, ShieldCheck, UserCog, Crown, Pencil, KeyRound } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";

const emptyStaff = { name: "", email: "", password: "", permissions: [] as string[] };

type Perm = { key: string; label: string; description?: string };

// Lista de permisos con lo que abre cada uno, para que quien asigna sepa qué está dando.
function PermissionPicker({ perms, value, onChange, prefix }: { perms: Perm[]; value: string[]; onChange: (v: string[]) => void; prefix: string }) {
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  return (
    <fieldset>
      <legend className="text-slate-700 text-sm mb-2">Secciones a las que tendrá acceso</legend>
      <div className="grid gap-2">
        {perms.map((p) => (
          <label key={p.key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
            <Checkbox data-testid={`${prefix}-${p.key}`} checked={value.includes(p.key)} onCheckedChange={() => toggle(p.key)} className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-slate-800">{p.label}</span>
              {p.description && <span className="block text-xs text-slate-600 mt-0.5">{p.description}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function AdminTeam() {
  usePageTitle("Equipo y permisos");
  const [team, setTeam] = useState<any[]>([]);
  const [perms, setPerms] = useState<Perm[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<any>(emptyStaff);
  const [editing, setEditing] = useState<any>(null);
  const [editForm, setEditForm] = useState<{ name: string; email: string; permissions: string[] }>({ name: "", email: "", permissions: [] });
  const [resetting, setResetting] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");
  const [toDelete, setToDelete] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setFailed(false);
    return Promise.all([api.get("/admin/team"), api.get("/admin/permissions")]).then(([t, p]) => {
      setTeam(t.data); setPerms(p.data);
    }).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/team", form);
      toast.success(`${form.name} ya puede entrar con su correo y la contraseña temporal. Compártela por un canal privado.`);
      setCreating(false); setForm(emptyStaff); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/team/${editing.id}/profile`, { name: editForm.name, email: editForm.email });
      await api.put(`/admin/team/${editing.id}/permissions`, { permissions: editForm.permissions });
      toast.success("Cambios guardados. Se aplican la próxima vez que la persona cargue el panel.");
      setEditing(null); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/team/${resetting.id}/password`, { password: newPassword });
      toast.success(`Contraseña de ${resetting.name} actualizada. Compártela por un canal privado.`);
      setResetting(null); setNewPassword("");
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/team/${toDelete.id}`); toast.success(`${toDelete.name} ya no tiene acceso al panel`); load(); }
    catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const permLabel = (k: string) => perms.find((p) => p.key === k)?.label || k;

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-team-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Equipo y permisos</h1>
          <p className="text-sm text-slate-600 mt-1">Da acceso al panel a otras personas y elige qué secciones puede usar cada una.</p>
        </div>
        <Button data-testid="new-staff-button" onClick={() => { setCreating(true); setForm(emptyStaff); }} className="gold-gradient-bg text-white font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuevo miembro
        </Button>
      </div>

      {failed ? (
        <LoadError what="el equipo" onRetry={load} />
      ) : (
        <ul className="grid gap-4">
          {team.map((m) => (
            <li key={m.id} data-testid="team-member-row" className="card-obsidian p-5 flex flex-wrap items-center gap-4">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${m.is_owner ? "bg-gold/15" : "bg-slate-100"}`}>
                {m.is_owner ? <Crown className="w-5 h-5 text-gold-ink" aria-hidden="true" /> : <UserCog className="w-5 h-5 text-slate-700" aria-hidden="true" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-semibold text-slate-900">{m.name}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${m.is_owner ? "bg-gold/15 text-gold-ink" : "bg-blue-500/15 text-blue-800"}`}>
                    {m.is_owner ? "Administrador principal" : "Miembro del equipo"}
                  </span>
                </div>
                <p className="text-sm text-slate-600">{m.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {m.is_owner ? (
                    <span className="text-sm text-slate-600 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-gold-ink" aria-hidden="true" /> Acceso a todo el panel</span>
                  ) : m.permissions.length === 0 ? (
                    <span className="text-sm text-amber-800">Sin secciones asignadas: no puede usar el panel</span>
                  ) : (
                    m.permissions.filter((p: string) => p !== "team").map((p: string) => (
                      <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{permLabel(p)}</span>
                    ))
                  )}
                </div>
              </div>
              {!m.is_owner && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button data-testid="edit-perms-button" size="sm" variant="outline" onClick={() => { setEditing(m); setEditForm({ name: m.name, email: m.email, permissions: m.permissions.filter((p: string) => p !== "team") }); }} className="border-slate-200 text-slate-700 hover:bg-slate-100 h-9">
                    <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setResetting(m); setNewPassword(""); }} className="border-slate-200 text-slate-700 hover:bg-slate-100 h-9">
                    <KeyRound className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Cambiar contraseña
                  </Button>
                  <Button data-testid="delete-staff-button" size="sm" variant="outline" onClick={() => setToDelete(m)} aria-label={`Quitar acceso a ${m.name}`} className="border-red-500/30 text-red-700 hover:bg-red-500/10 h-9">
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Nuevo miembro */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nuevo miembro del equipo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="staff-name" className="text-slate-700 text-sm">Nombre *</Label>
              <Input id="staff-name" data-testid="staff-name-input" value={form.name} onChange={(e: any) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            <div>
              <Label htmlFor="staff-email" className="text-slate-700 text-sm">Correo *</Label>
              <Input id="staff-email" data-testid="staff-email-input" type="email" value={form.email} onChange={(e: any) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            <div>
              <Label htmlFor="staff-password" className="text-slate-700 text-sm">Contraseña temporal *</Label>
              <Input id="staff-password" data-testid="staff-password-input" type="text" autoComplete="off" value={form.password} onChange={(e: any) => setForm({ ...form, password: e.target.value })} aria-describedby="staff-password-help" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              <p id="staff-password-help" className="text-xs text-slate-600 mt-1">Mínimo 6 caracteres. Compártela con la persona por un canal privado; podrás cambiarla después desde esta pantalla.</p>
            </div>
            <PermissionPicker perms={perms} value={form.permissions} onChange={(v) => setForm({ ...form, permissions: v })} prefix="perm" />
            <p className="text-xs text-slate-600">* Campos obligatorios</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-staff-button" onClick={create} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Crear miembro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar miembro */}
      <Dialog open={!!editing} onOpenChange={(o: boolean) => !o && setEditing(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Editar a {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-staff-name" className="text-slate-700 text-sm">Nombre</Label>
                <Input id="edit-staff-name" value={editForm.name} onChange={(e: any) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              </div>
              <div>
                <Label htmlFor="edit-staff-email" className="text-slate-700 text-sm">Correo</Label>
                <Input id="edit-staff-email" type="email" value={editForm.email} onChange={(e: any) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              </div>
            </div>
            <PermissionPicker perms={perms} value={editForm.permissions} onChange={(v) => setEditForm({ ...editForm, permissions: v })} prefix="editperm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-perms-button" onClick={saveEdit} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cambiar contraseña */}
      <Dialog open={!!resetting} onOpenChange={(o: boolean) => !o && setResetting(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nueva contraseña para {resetting?.name}</DialogTitle></DialogHeader>
          <div>
            <Label htmlFor="reset-password" className="text-slate-700 text-sm">Contraseña temporal</Label>
            <Input id="reset-password" type="text" autoComplete="off" value={newPassword} onChange={(e: any) => setNewPassword(e.target.value)} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            <p className="text-xs text-slate-600 mt-1">Mínimo 6 caracteres. La contraseña anterior deja de funcionar al guardar.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button onClick={resetPassword} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        testid="delete-staff-dialog"
        danger
        title={`¿Quitar el acceso a ${toDelete?.name || ""}?`}
        description={<p>{toDelete?.name} ({toDelete?.email}) ya no podrá entrar al panel. Su cuenta se elimina y no se puede recuperar; las decisiones que tomó siguen en el historial.</p>}
        confirmLabel="Quitar acceso"
        onConfirm={remove}
      />
    </div>
  );
}
